import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, 
  Star, 
  Quote, 
  MapPin, 
  Briefcase, 
  CheckCircle,
  Phone,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { getMediaUrl } from '../utils/mediaUrl';
import OptimizedImage from '../components/OptimizedImage';
import { useSchemaOrg } from '../hooks/useSchemaOrg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SingleCasePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCase();
  }, [slug]);

  const fetchCase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cases/slug/${slug}`);
      setCaseData(response.data);
    } catch (err) {
      console.error('Error fetching case:', err);
      setError('Kunne ikke finne denne referansen');
    } finally {
      setLoading(false);
    }
  };

  // Render stars
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
      />
    ));
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Laster kundecase...</div>
        </div>
      </Layout>
    );
  }

  if (error || !caseData) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Referanse ikke funnet</h1>
            <p className="text-gray-400 mb-6">{error || 'Denne referansen finnes ikke'}</p>
            <Link to="/referanser">
              <Button className="bg-red-600 hover:bg-red-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Tilbake til referanser
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const { name: companyName, publisher } = useSchemaOrg();

  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": publisher,
    "author": {
      "@type": "Organization",
      "name": caseData.customer_name,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": caseData.location
      }
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": caseData.rating,
      "bestRating": "5",
      "worstRating": "1"
    },
    "name": caseData.title,
    "reviewBody": caseData.quote,
    "datePublished": caseData.date_published
  };

  return (
    <Layout>
      <Helmet>
        <title>{caseData.meta_title || caseData.title} | Sand-tec AS</title>
        <meta name="description" content={caseData.meta_description || caseData.quote} />
        <link rel="canonical" href={`${BACKEND_URL}/referanse/${slug}`} />
        
        <meta property="og:title" content={caseData.meta_title || caseData.title} />
        <meta property="og:description" content={caseData.meta_description || caseData.quote} />
        <meta property="og:type" content="article" />
        {caseData.featured_image && (
          <meta property="og:image" content={getMediaUrl(caseData.featured_image)} />
        )}
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #ca8a04', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#ca8a04', margin: '0 0 8px'}}>{caseData?.title}</h1>
            {caseData?.quote && <blockquote style={{fontSize: '1.1em', color: '#555', margin: '10px 0', fontStyle: 'italic', borderLeft: '4px solid #ca8a04', paddingLeft: '15px'}}>"{caseData.quote}"</blockquote>}
            <p style={{fontSize: '0.85em', color: '#999'}}>
              {caseData?.company_name && `${caseData.company_name}`}
              {caseData?.industry && ` — ${caseData.industry}`}
              {caseData?.location && ` — ${caseData.location}`}
            </p>
          </header>

          {caseData?.featured_image && (
            <div style={{marginBottom: '25px'}}>
              <img src={getMediaUrl(caseData.featured_image)} alt={caseData?.title} style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>
          )}

          {caseData?.full_story && (
            <article style={{marginBottom: '30px', lineHeight: '1.8', color: '#374151'}}>
              <div dangerouslySetInnerHTML={{__html: caseData.full_story}} />
            </article>
          )}

          {caseData?.results && caseData.results.length > 0 && (
            <section style={{marginBottom: '30px'}}>
              <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Oppnådde resultater</h2>
              <ul style={{lineHeight: '2', color: '#374151', paddingLeft: '20px'}}>
                {caseData.results.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </section>
          )}

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Se også</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/referanser" style={{color: '#ca8a04'}}>Alle referanser</a></li>
              <li><a href="/tjenester" style={{color: '#ca8a04'}}>Tjenester</a></li>
              <li><a href="/anmeldelser" style={{color: '#ca8a04'}}>Anmeldelser</a></li>
              <li><a href="/kontakt" style={{color: '#ca8a04'}}>Kontakt</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>Kontakt oss for mer informasjon.</p>
            <p style={{marginTop: '5px'}}>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#ca8a04'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#ca8a04'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Hero Section */}
        <div className="relative pt-32 pb-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(234,179,8,0.1),transparent_50%)]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Back Button */}
            <button
              onClick={() => navigate('/referanser')}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake til referanser</span>
            </button>

            {/* Meta Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {caseData.industry && (
                <span className="inline-flex items-center px-3 py-1 text-sm bg-blue-600/20 text-blue-400 rounded-full">
                  <Briefcase className="w-4 h-4 mr-1" />
                  {caseData.industry}
                </span>
              )}
              {caseData.location && (
                <span className="inline-flex items-center px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded-full">
                  <MapPin className="w-4 h-4 mr-1" />
                  {caseData.location}
                </span>
              )}
              {caseData.service_used && (
                <span className="inline-flex items-center px-3 py-1 text-sm bg-red-600/20 text-red-400 rounded-full">
                  {caseData.service_used}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {caseData.title}
            </h1>

            {/* Customer Name */}
            <p className="text-xl text-gray-400 mb-6">
              {caseData.customer_name}
            </p>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex">{renderStars(caseData.rating)}</div>
              {caseData.date_published && (
                <span className="text-gray-500 text-sm flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(caseData.date_published).toLocaleDateString('nb-NO', { 
                    year: 'numeric', 
                    month: 'long' 
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Featured Image */}
              {(caseData.featured_image || (caseData.image_before && caseData.image_after)) && (
                <div className="rounded-xl overflow-hidden">
                  {caseData.featured_image ? (
                    <OptimizedImage
                      src={caseData.featured_image}
                      alt={caseData.title}
                      className="w-full h-auto"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <OptimizedImage
                          src={caseData.image_before}
                          alt={`${caseData.customer_name} - Før`}
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <span className="absolute bottom-2 left-2 px-3 py-1 bg-black/70 text-white text-sm rounded">Før</span>
                      </div>
                      <div className="relative">
                        <OptimizedImage
                          src={caseData.image_after}
                          alt={`${caseData.customer_name} - Etter`}
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <span className="absolute bottom-2 right-2 px-3 py-1 bg-green-600 text-white text-sm rounded">Etter</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* The Socratic Quote */}
              <blockquote className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-8">
                <Quote className="absolute top-4 left-4 w-12 h-12 text-yellow-600/20" />
                <p className="text-xl md:text-2xl text-white italic leading-relaxed pl-8">
                  "{caseData.quote}"
                </p>
                <footer className="mt-6 pl-8 flex items-center gap-4">
                  <div>
                    <p className="text-white font-semibold">{caseData.author_name}</p>
                    <p className="text-gray-400 text-sm">{caseData.author_title}, {caseData.customer_name}</p>
                  </div>
                </footer>
              </blockquote>

              {/* Challenge */}
              {caseData.challenge && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center text-red-400">1</span>
                    Utfordringen
                  </h2>
                  <p className="text-gray-300 leading-relaxed">{caseData.challenge}</p>
                </section>
              )}

              {/* Solution */}
              {caseData.solution && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-yellow-600/20 rounded-full flex items-center justify-center text-yellow-400">2</span>
                    Løsningen
                  </h2>
                  <p className="text-gray-300 leading-relaxed">{caseData.solution}</p>
                </section>
              )}

              {/* Results */}
              {caseData.results && caseData.results.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-green-600/20 rounded-full flex items-center justify-center text-green-400">3</span>
                    Resultater
                  </h2>
                  <ul className="space-y-3">
                    {caseData.results.map((result, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-300">
                        <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{result}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* CTA Card */}
                <div className="bg-gradient-to-br from-red-900/30 to-yellow-900/30 border border-red-600/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-3">
                    Vil du oppnå lignende resultater?
                  </h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Kontakt oss for en uforpliktende prat om hvordan vi kan hjelpe din virksomhet.
                  </p>
                  <Link to="/kontakt">
                    <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800">
                      <Phone className="w-4 h-4 mr-2" />
                      Kontakt oss
                    </Button>
                  </Link>
                </div>

                {/* Quick Facts */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Om kunden</h3>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-gray-500">Bedrift</dt>
                      <dd className="text-white">{caseData.customer_name}</dd>
                    </div>
                    {caseData.industry && (
                      <div>
                        <dt className="text-gray-500">Bransje</dt>
                        <dd className="text-white">{caseData.industry}</dd>
                      </div>
                    )}
                    {caseData.location && (
                      <div>
                        <dt className="text-gray-500">Sted</dt>
                        <dd className="text-white">{caseData.location}</dd>
                      </div>
                    )}
                    {caseData.service_used && (
                      <div>
                        <dt className="text-gray-500">Tjeneste</dt>
                        <dd className="text-white">{caseData.service_used}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Other Cases Link */}
                <Link 
                  to="/referanser"
                  className="block text-center text-red-400 hover:text-red-300 text-sm"
                >
                  Se alle referanser →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SingleCasePage;
